import type { FactionId, ShipClassId, TechId } from './ids';

/**
 * Ships and fleets (GDD §8). Combat stats arrive with combat (#22).
 * Prices are in ГС, paid in the player's currency at the current rate.
 * Starting values for tuning.
 */
export interface ShipClassDef {
  readonly id: ShipClassId;
  /** i18n key: `ship.<id>.name`. */
  readonly i18nKey: `ship.${ShipClassId}`;
  /** Only this faction can build it; null = everyone. */
  readonly faction: FactionId | null;
  /** Technology that unlocks it (#27); null = available from the start. */
  readonly requiresTech: TechId | null;
  readonly cost: { readonly cash: number; readonly alloys: number };
  /** Construction time in the spaceport, ticks. */
  readonly buildTicks: number;
  /** Ticks to cross one hex (GDD §2: 3–5 s for a basic ship). A fleet moves at its slowest ship. */
  readonly ticksPerHex: number;
  /** Goods it can carry (caravans, raids). */
  readonly cargo: number;
  /** Base upkeep per cycle before the fleet-size multiplier. */
  readonly upkeep: { readonly cash: number; readonly alloys: number; readonly energy: number };
}

const ship = (
  id: ShipClassId,
  def: Omit<ShipClassDef, 'id' | 'i18nKey' | 'faction' | 'requiresTech' | 'cargo'> &
    Partial<Pick<ShipClassDef, 'faction' | 'requiresTech' | 'cargo'>>,
): ShipClassDef => ({
  id,
  i18nKey: `ship.${id}`,
  faction: null,
  requiresTech: null,
  cargo: 0,
  ...def,
});

export const SHIP_CLASSES: Readonly<Record<ShipClassId, ShipClassDef>> = {
  explorer: ship('explorer', {
    cost: { cash: 150, alloys: 10 },
    buildTicks: 300,
    ticksPerHex: 30,
    upkeep: { cash: 3, alloys: 0, energy: 0.25 },
  }),
  transport: ship('transport', {
    cost: { cash: 200, alloys: 20 },
    buildTicks: 300,
    ticksPerHex: 50,
    cargo: 40,
    upkeep: { cash: 4, alloys: 0.5, energy: 0.5 },
  }),
  corvette: ship('corvette', {
    cost: { cash: 250, alloys: 30 },
    buildTicks: 450,
    ticksPerHex: 30,
    upkeep: { cash: 6, alloys: 1, energy: 0.5 },
  }),
  frigate: ship('frigate', {
    cost: { cash: 400, alloys: 50 },
    buildTicks: 600,
    ticksPerHex: 40,
    upkeep: { cash: 10, alloys: 1.5, energy: 1 },
  }),
  cruiser: ship('cruiser', {
    requiresTech: 'heavyHulls',
    cost: { cash: 700, alloys: 90 },
    buildTicks: 900,
    ticksPerHex: 50,
    upkeep: { cash: 16, alloys: 2.5, energy: 1.5 },
  }),
  // Earthborn: a caravan ship with 25% more cargo (GDD §4).
  tradeLiner: ship('tradeLiner', {
    faction: 'earthborn',
    cost: { cash: 300, alloys: 25 },
    buildTicks: 450,
    ticksPerHex: 45,
    cargo: 50,
    upkeep: { cash: 5, alloys: 0.5, energy: 0.5 },
  }),
  // Reforged: fast, boards its prey (boarding arrives with raids, #24).
  raider: ship('raider', {
    faction: 'reforged',
    cost: { cash: 300, alloys: 35 },
    buildTicks: 450,
    ticksPerHex: 25,
    cargo: 20,
    upkeep: { cash: 7, alloys: 1, energy: 0.5 },
  }),
  // Lands troops on an enemy capital (#135, the author's call of 26.10.2026: the
  // constructor carried them before). Unarmed; the landing spends it.
  lander: ship('lander', {
    requiresTech: 'orbitalAssault',
    cost: { cash: 250, alloys: 30 },
    buildTicks: 450,
    ticksPerHex: 40,
    upkeep: { cash: 5, alloys: 0.5, energy: 0.5 },
  }),
};

export const FLEET = {
  /**
   * Upkeep grows faster than the fleet: every ship's upkeep is multiplied
   * by N^exponent, N = ships the player owns (GDD §8, a brake on snowballing).
   */
  upkeepExponent: 0.3,
  /** Ships beyond the spaceport's limit cost this much more each. */
  overLimitMultiplier: 2,
  /** Fleet limit by spaceport level 1/2/3. */
  limitBySpaceportLevel: [6, 10, 14],
  /** Ships the spaceport builds at once by level 1/2/3 (GDD §5: parallel assembly). */
  parallelBySpaceportLevel: [1, 1, 2],
  /** Orders waiting in the spaceport queue, at most. */
  maxQueue: 5,
  /** Fleets of a player that could not pay upkeep crawl at this share of their speed. */
  unsuppliedSpeed: 0.5,
} as const;
